// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";

import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import type { StandardQuestion } from "@modules/service/reducer/standard-questions/types";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";

import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "@components";
import { HierarchyScopeForm } from "@modules/service/components/hierarchy-scope";
import type { HierarchyAssignment } from "@modules/service/types";
import type { State } from "@store/reducers";

type PropsFromState = $Exact<{
  container: ?StandardQuestion,
  hierarchy: ?HierarchyTree,
  draftAssignments: Array<HierarchyAssignment>,
  isDraftAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setShowEditAssignmentsModal: typeof actionCreators.ShowEditStandardQuestionAssignmentsModal,
  updateStandardQuestionDraftAssignments: typeof actionCreators.SetStandardQuestionAssignments,
  updateAssignments: typeof actionCreators.UpdateStandardQuestionAssignments,
}>;

type OwnProps = $Exact<{
  clientId: number,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EditStandardQuestionAssignmentsModal extends Component<Props> {
  handleCancel = () => {
    this.props.setShowEditAssignmentsModal(false);
    this.props.updateStandardQuestionDraftAssignments([]);
  };

  handleSubmitEapDraft = () => {
    if (this.props.container && this.props.container.hierarchyAssignments) {
      this.props.updateAssignments({
        container: this.props.container,
        assignments: this.props.draftAssignments,
      });
    }
  };

  handleChanges = (data: Array<HierarchyAssignment>) => {
    this.props.updateStandardQuestionDraftAssignments(data);
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EDIT_STANDARD_QUESTION_MODAL_TITLE", {
            ...(this.props.container ? { providerId: this.props.container.id } : {}),
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <div className="w-100 d-flex justify-content-center">
            <div className="col-sm-12">
              {this.props.hierarchy && this.props.draftAssignments ? (
                <HierarchyScopeForm
                  hierarchy={this.props.hierarchy}
                  assignments={this.props.draftAssignments}
                  onChange={this.handleChanges}
                />
              ) : null}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            onClick={this.handleSubmitEapDraft}
            disabled={!this.props.isDraftAssignmentsValid}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    container: serviceSelectors.getStandardQuestionContainer(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    draftAssignments: serviceSelectors.getStandardQuestionDraftAssignments(state),
    isDraftAssignmentsValid: serviceSelectors.isStandardQuestionDraftAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setShowEditAssignmentsModal: actionCreators.ShowEditStandardQuestionAssignmentsModal,
  updateStandardQuestionDraftAssignments: actionCreators.SetStandardQuestionDraftAssignments,
  updateAssignments: actionCreators.UpdateStandardQuestionAssignments,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EditStandardQuestionAssignmentsModal);
